/**
 * FRONT-1567: turns a `FilePickerOption` — something the user picked from
 * the "attach an existing file" chooser (an upload OR a saved Knowledge
 * document) — into a ready-to-stage chat attachment, with NO upload call
 * (the file already exists; only a reference is needed). Pure, dependency-
 * free, mirrors the `plan-status.ts` / `image-source.ts` precedent for a
 * small chat-feature derivation kept out of `chat-input.tsx` and
 * independently unit-tested (this repo's frontend suite runs under plain
 * node, no jsdom — see `picked-file-attachment.test.ts`).
 */
import type { FilePickerOption } from '~/components/file-picker';

/** The subset of a `ready` `PendingAttach` this derivation owns — the
 *  caller (`chat-input.tsx`) adds `state: 'ready'` and a fresh `localId`. */
export interface PickedFileAttachment {
  fileId: string;
  name: string;
  contentType: string;
  kind: 'text' | 'image' | 'pdf' | 'binary';
  source: 'upload' | 'document';
}

/**
 * A saved document has no `contentType`/`kind` columns to begin with (see
 * `AttachmentFileRow`'s docblock in `src/agents/chat-attachments.ts`) — the
 * server always treats a document's body as markdown text, so that's what a
 * picked document is staged as. An upload carries its real contentType/kind
 * through `FilePickerOption` (populated by `deriveFilePickerOptions`);
 * falls back to a generic binary only if those are somehow missing.
 */
export function pickedFileAttachment(option: FilePickerOption): PickedFileAttachment {
  if (option.source === 'document') {
    return {
      fileId: option.ref,
      name: option.name,
      contentType: 'text/markdown',
      kind: 'text',
      source: 'document',
    };
  }
  return {
    fileId: option.ref,
    name: option.name,
    contentType: option.contentType ?? 'application/octet-stream',
    kind: option.kind ?? 'binary',
    source: 'upload',
  };
}
